import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileText,
  Loader2,
  Monitor,
  Plus,
  RefreshCw,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import Sidebar from '../../components/sidebar/Sidebar';
import { ResponsiveAppShell, TaskyButton, TaskyPageContainer, TaskyTopBar } from '../../components/tasky';
import LpWorkflowCreate from '../../components/lp/LpWorkflowCreate';
import LpWorkflowFields from '../../components/lp/LpWorkflowFields';
import LegacyLpCreationPage from './LpCreationPage';
import lpGenerationService, {
  LpProject,
} from '../../services/api/lpGenerationService';
import service, {
  LP_STAGES,
  LP_STAGE_LABELS,
  LpDesign,
  LpStage,
  LpGenerationStage,
  LpWorkflowStages,
  LpWorkflowProject,
} from '../../services/api/lpWorkflowService';
import './LpWorkflowPage.css';

const POLLING_ERROR =
  '生成状況を取得できません。通信が戻り次第、再確認します。';

const DESCRIPTIONS: Record<LpStage, string> = {
  brief: '商品の事実と、伝える相手・目的を整理します。',
  strategy: 'この商品が選ばれる理由を、どの切り口で伝えますか？',
  outline: '読み手の疑問が解消される順番に、情報を並べます。',
  copy: '見出し・本文・ボタンの文言を、公開する言葉に整えます。',
  design: '確定したコピーと商品素材で、見た目を選びます。',
  review: '商品の事実、表示、行動先を確認して仕上げます。',
};
const editable = (project: LpWorkflowProject): LpWorkflowStages => {
  const { brief, strategy, outline, copy, design, review } = project.workflow;
  return JSON.parse(
    JSON.stringify({ brief, strategy, outline, copy, design, review }),
  );
};
const activeOperation = (project: LpWorkflowProject | null) => {
  const operation = project?.workflow.operation;
  if (!operation || !['pending', 'processing'].includes(operation.status))
    return false;
  return Date.now() - new Date(operation.started_at).getTime() < 20 * 60 * 1000;
};

export default function LpWorkflowPage() {
  const [params, setParams] = useSearchParams();
  const id = params.get('project');
  const [collapsed, setCollapsed] = useState(false);
  const [projects, setProjects] = useState<LpProject[]>([]);
  const [project, setProject] = useState<LpWorkflowProject | null>(null);
  const [legacy, setLegacy] = useState(params.get('legacy') === '1');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState<LpStage>('brief');
  const [draft, setDraft] = useState<LpWorkflowStages | null>(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const projectRef = useRef(project);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [instruction, setInstruction] = useState('');
  const [preview, setPreview] = useState('');
  const [previewName, setPreviewName] = useState('');
  const previewRef = useRef<HTMLDivElement>(null);
  const [candidates, setCandidates] = useState<Record<string, string>>({});
  const [mobile, setMobile] = useState(false);
  const [batch, setBatch] = useState<string[]>([]);
  const pollingErrorRef = useRef(0);
  const pageIdRef = useRef(id);
  pageIdRef.current = id;
  projectRef.current = project;
  dirtyRef.current = dirty;

  useEffect(() => {
    if (preview) previewRef.current?.scrollIntoView?.({ block: 'start' });
  }, [preview]);

  const list = useCallback(() => {
    lpGenerationService
      .getProjects()
      .then((r) => {
        if (r.success) setProjects(r.data);
      })
      .catch(() =>
        setError('LP一覧を読み込めませんでした。再読込してください。'),
      );
  }, []);
  useEffect(() => {
    list();
  }, [list]);

  useEffect(() => {
    if (!id || legacy) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDirty(false);
    setPreview('');
    setCandidates({});
    setBatch([]);
    lpGenerationService
      .getProject(id)
      .then(async (result) => {
        if (cancelled) return;
        if (!result.data.workflow) {
          setLegacy(true);
          return;
        }
        const next = await service.get(id);
        if (cancelled) return;
        setProject(next);
        setDraft(editable(next));
        if (next.workflow.operation?.status === 'failed')
          setError(
            next.workflow.operation.error ||
              '前回の生成に失敗しました。この工程から再試行できます。',
          );
        setStage(
          LP_STAGES.find((s) => !next.workflow.confirmed[s]) ?? 'review',
        );
      })
      .catch((e) => {
        if (!cancelled) setError(service.error(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, legacy]);

  // Poll only while generation is active. Never replace a local editing draft on a status-only response.
  const generating = activeOperation(project);
  useEffect(() => {
    if (!id || !generating) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const next = await service.get(id);
        if (cancelled) return;
        pollingErrorRef.current = 0;
        setError((previous) => (previous === POLLING_ERROR ? '' : previous));
        const previousRevision = projectRef.current?.workflow.revision;
        setProject(next);
        if (next.workflow.revision !== previousRevision && !dirtyRef.current) {
          setDraft(editable(next));
          setCandidates({});
          setPreview('');
        }
        if (!activeOperation(next)) {
          if (next.workflow.operation?.status === 'failed') {
            setError(
              next.workflow.operation.error ||
                '生成に失敗しました。再試行できます。',
            );
            setBatch([]);
          } else
            setNotice('提案を保存しました。内容を確認・修正して次へ進めます。');
          return;
        }
      } catch {
        if (cancelled) return;
        pollingErrorRef.current += 1;
        if (pollingErrorRef.current >= 3) setError(POLLING_ERROR);
      }
      timer = setTimeout(poll, 3000);
    };
    timer = setTimeout(poll, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [id, generating]);

  const accept = (next: LpWorkflowProject) => {
    if (pageIdRef.current !== next.id) return;
    setProject(next);
    setDraft(editable(next));
    setDirty(false);
    setPreview('');
    setCandidates({});
  };
  const saveDraft = async (
    confirm: boolean,
    value = draft?.[stage],
  ): Promise<LpWorkflowProject | null> => {
    if (!project || !value) return null;
    const next = await service.save(
      project.id,
      project.workflow.revision,
      stage,
      value,
      confirm,
    );
    accept(next);
    return next;
  };
  const save = async (confirm: boolean) => {
    if (busy || generating) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const next = await saveDraft(confirm);
      if (next) {
        if (confirm && stage !== 'review')
          setStage(LP_STAGES[LP_STAGES.indexOf(stage) + 1]);
        setNotice(
          confirm && stage === 'copy' && activeOperation(next)
            ? 'デザイン案3つと全体デザイン画像を自動で作成しています。生成クレジットを消費します。このページを離れても続きます。'
            : confirm
            ? stage === 'review'
              ? '最終確認が完了しました。画像を含むHTMLを書き出せます。'
              : '内容を確定しました。次の工程を提案できます。'
            : '下書きを保存しました。',
        );
        setInstruction('');
      }
    } catch (e) {
      setError(service.error(e));
    } finally {
      setBusy(false);
    }
  };
  const generate = async (
    target: LpGenerationStage,
    sectionId?: string,
    designId?: string,
    designDraft?: LpDesign,
  ) => {
    if (!project || busy || generating) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const current =
        dirty || designDraft
          ? await saveDraft(false, designDraft ?? draft?.[stage])
          : project;
      if (!current) return;
      const next = await service.generate(
        current.id,
        current.workflow.revision,
        target,
        instruction,
        sectionId,
        designId,
      );
      accept(next);
      setInstruction('');
      const tasks = next.workflow.operation?.tasks;
      const createsImages = tasks?.length
        ? tasks.some((task) => task.kind === 'image' || task.kind === 'concept')
        : ['image', 'concept', 'concepts', 'design_images'].includes(target);
      setNotice(
        createsImages
          ? '画像の生成を開始しました。生成クレジットを消費します。'
          : tasks?.some((task) => task.kind === 'code')
            ? 'LPのHTML/CSSを組み立てています。完成済みの画像は保持します。'
            : 'AIの提案を生成しています。このページを離れても処理は続きます。',
      );
    } catch (e) {
      setError(service.error(e));
      setBatch([]);
    } finally {
      setBusy(false);
    }
  };
  // Serialize paid image requests. A failure stops the batch; already completed images remain usable.
  useEffect(() => {
    if (!batch.length || busy || generating || !project) return;
    const nextId = batch[0];
    setBatch((old) => old.slice(1));
    void generate('image', nextId);
    // generate intentionally reads the latest render's revision; all requests are serialized by busy/generating.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch, busy, generating, project]);

  // A card's build button adopts that design first, so section images and the final LP follow the chosen card.
  const build = (designId: string) => {
    if (!draft?.design) return;
    const adopt =
      dirty || project?.workflow.design?.selected_id !== designId
        ? { ...draft.design, selected_id: designId }
        : undefined;
    void generate('design_images', undefined, designId, adopt);
  };
  const attach = async (sectionId: string, key: string | null) => {
    if (!project || busy || generating) return;
    setBusy(true);
    setError('');
    try {
      const current = dirty ? await saveDraft(false) : project;
      if (current)
        accept(
          await service.attach(
            current.id,
            current.workflow.revision,
            sectionId,
            key,
          ),
        );
    } catch (e) {
      setError(service.error(e));
    } finally {
      setBusy(false);
    }
  };
  const showPreview = async (candidate?: string) => {
    if (!project || busy || generating) return;
    setBusy(true);
    setError('');
    try {
      const current = dirty ? await saveDraft(false) : project;
      if (!current) return;
      const html = await service.preview(current.id, candidate, false);
      if (pageIdRef.current !== current.id) return;
      if (candidate) setCandidates((old) => ({ ...old, [candidate]: html }));
      setPreviewName(
        current.workflow.design?.options.find(
          (o) => o.id === (candidate ?? current.workflow.design?.selected_id),
        )?.name ?? '',
      );
      setPreview(html);
    } catch (e) {
      setError(service.error(e));
    } finally {
      setBusy(false);
    }
  };
  const download = async () => {
    if (!project || busy || generating || dirty) return;
    setBusy(true);
    setError('');
    try {
      const html = await service.preview(project.id, undefined, false, true);
      const url = URL.createObjectURL(
        new Blob([html], { type: 'text/html;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(project.title || 'landing-page').replace(/[\\/:*?"<>|]/g, '-')}.html`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(service.error(e));
    } finally {
      setBusy(false);
    }
  };
  const change = <K extends LpStage>(
    target: K,
    value: NonNullable<LpWorkflowStages[K]>,
  ) => {
    setDraft((old) => (old ? { ...old, [target]: value } : old));
    setDirty(true);
    setCandidates({});
    setPreview('');
    setNotice('');
  };
  const open = (nextId: string | null) => {
    if (dirty) {
      setError('変更を保存・確定してから移動してください。');
      return;
    }
    setProject(null);
    setDraft(null);
    setLegacy(false);
    setError('');
    setNotice('');
    setPreview('');
    setBatch([]);
    setParams(nextId ? { project: nextId } : {});
  };
  const created = (next: LpWorkflowProject) => {
    setProject(next);
    setDraft(editable(next));
    setStage('brief');
    setParams({ project: next.id });
    list();
  };
  const reload = async () => {
    if (!id) {
      list();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next = await service.get(id);
      if (dirty) {
        setProject(next);
        setNotice(
          '最新の状態を取得しました。入力中の変更は保持しています。内容を確認して保存してください。',
        );
      } else accept(next);
    } catch (e) {
      setError(service.error(e));
    } finally {
      setBusy(false);
    }
  };

  if (legacy)
    return (
      <>
        <div className="lpw-legacy-return">
          <button className="lpw-button secondary" onClick={() => open(null)}>
            <ArrowLeft size={16} />
            6段階のLP制作へ
          </button>
        </div>
        <LegacyLpCreationPage />
      </>
    );
  const pageActions = (
    <>
      <TaskyButton
        variant="secondary"
        size="sm"
        onClick={() => void reload()}
        disabled={busy || loading}
      >
        <RefreshCw size={16} />
        再読込
      </TaskyButton>
      {id && (
        <TaskyButton
          variant="secondary"
          size="sm"
          onClick={() => open(null)}
          disabled={busy}
        >
          <Plus size={16} />
          LP一覧・新規作成
        </TaskyButton>
      )}
    </>
  );

  return (
    <ResponsiveAppShell
      sidebar={
        <Sidebar collapsed={collapsed} onToggleCollapse={setCollapsed} />
      }
      mobileSidebar={
        <Sidebar collapsed={false} onToggleCollapse={() => undefined} />
      }
      topbarTitle="LP制作"
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        <TaskyTopBar
          title={project?.title || 'LP制作'}
          icon={<FileText className="w-5 h-5" />}
          sidebarCollapsed={collapsed}
          onExpandSidebar={() => setCollapsed(false)}
        >
          {pageActions}
        </TaskyTopBar>
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
      <TaskyPageContainer className="lpw-page">
        {/* lg 未満は共通トップバーが出ないため、ページ固有の操作を本文先頭に出す */}
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2 lg:hidden">
          {project?.title && <p className="m-0 mr-auto min-w-0 truncate text-sm font-semibold text-dark">{project.title}</p>}
          <div className="flex gap-2">{pageActions}</div>
        </div>
        {error && (
          <div className="lpw-error" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <div className="lpw-success" role="status">
            {notice}
          </div>
        )}
        {loading ? (
          <div className="lpw-empty">
            <Loader2 className="animate-spin" />
            LPを読み込んでいます
          </div>
        ) : project && draft ? (
          <>
            <nav className="lpw-steps" aria-label="LPの制作工程">
              {LP_STAGES.map((key, index) => {
                const ready = LP_STAGES.slice(0, index).every(
                  (s) => project.workflow.confirmed[s],
                );
                return (
                  <button
                    key={key}
                    aria-current={stage === key ? 'step' : undefined}
                    disabled={
                      busy ||
                      generating ||
                      batch.length > 0 ||
                      (!ready && !project.workflow[key])
                    }
                    onClick={() => {
                      if (dirty) {
                        setError(
                          '変更を保存・確定してから工程を移動してください。',
                        );
                        return;
                      }
                      setStage(key);
                      setError('');
                      setNotice('');
                      setInstruction('');
                    }}
                  >
                    <span
                      className={project.workflow.confirmed[key] ? 'done' : ''}
                    >
                      {project.workflow.confirmed[key] ? (
                        <Check size={15} />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <strong>{LP_STAGE_LABELS[key]}</strong>
                    <small>
                      {project.workflow.confirmed[key]
                        ? '確定済み'
                        : project.workflow[key]
                          ? '確認・編集'
                          : '未着手'}
                    </small>
                  </button>
                );
              })}
            </nav>
            <section className="lpw-card lpw-workspace">
              <div className="lpw-workspace-heading">
                <div>
                  <div className="lpw-kicker">
                    STEP {LP_STAGES.indexOf(stage) + 1}
                  </div>
                  <h2>{LP_STAGE_LABELS[stage]}</h2>
                  <p className="lpw-muted">{DESCRIPTIONS[stage]}</p>
                </div>
                {dirty && (
                  <span className="lpw-badge warning">未保存の変更</span>
                )}
              </div>
              {LP_STAGES.indexOf(stage) > 0 &&
                !LP_STAGES.slice(0, LP_STAGES.indexOf(stage)).every(
                  (s) => project.workflow.confirmed[s],
                ) && (
                  <div className="lpw-note">
                    前の工程に変更があります。確定してから、この工程を更新してください。
                  </div>
                )}
              {generating ? (
                <div className="lpw-generation" role="status">
                  <Loader2 className="animate-spin" />
                  <div>
                    <strong>
                      {[
                        'image',
                        'concept',
                        'concepts',
                        'design_images',
                        'design_code',
                      ].includes(project.workflow.operation?.stage || '')
                        ? project.workflow.operation?.tasks?.[
                            project.workflow.operation?.task_index ?? 0
                          ]?.kind === 'code'
                          ? 'LPのHTML/CSS'
                          : project.workflow.operation?.tasks?.[
                                project.workflow.operation?.task_index ?? 0
                              ]?.kind === 'concept'
                            ? '全体デザイン画像'
                            : 'セクション画像'
                        : LP_STAGE_LABELS[
                            (project.workflow.operation?.stage ||
                              stage) as LpStage
                          ]}
                      を生成しています
                    </strong>
                    <p>
                      {project.workflow.operation?.tasks
                        ? `${project.workflow.operation.tasks.filter((t) => t.status === 'completed').length} / ${project.workflow.operation.tasks.length}工程完了。完成分は保存済みです。このページを離れても続きます。`
                        : '結果は自動で表示されます。'}
                      {batch.length > 0 &&
                        `この後、残り${batch.length}枚を生成します。`}
                    </p>
                  </div>
                  {batch.length > 0 && (
                    <button
                      className="lpw-button secondary"
                      onClick={() => setBatch([])}
                    >
                      残りの生成を止める
                    </button>
                  )}
                </div>
              ) : (
                <div className="lpw-ai-bar">
                  <label className="lpw-field">
                    AIへの修正指示（任意）
                    <input
                      maxLength={3000}
                      value={instruction}
                      onChange={(e) => setInstruction(e.target.value)}
                      placeholder="例：比較検討中の人向けに、導入条件を分かりやすく"
                    />
                  </label>
                  <button
                    className="lpw-button secondary"
                    disabled={
                      busy ||
                      !LP_STAGES.slice(0, LP_STAGES.indexOf(stage)).every(
                        (s) => project.workflow.confirmed[s],
                      )
                    }
                    onClick={() => void generate(stage)}
                  >
                    <Sparkles size={16} />
                    {stage === 'review'
                      ? 'AIで整合を確認する'
                      : draft[stage] && stage !== 'brief'
                        ? 'AIで提案を作り直す'
                        : 'AIで提案する'}
                  </button>
                </div>
              )}
              <fieldset
                disabled={busy || generating || batch.length > 0}
                className="lpw-edit-fields"
              >
                <LpWorkflowFields
                  stage={stage}
                  draft={draft}
                  project={project}
                  onChange={change}
                  onGenerate={(target, sectionId, designId) =>
                    void generate(target, sectionId, designId)
                  }
                  onBuild={build}
                  onAttach={(sectionId, key) => void attach(sectionId, key)}
                  onPreview={(candidate) => void showPreview(candidate)}
                  candidatePreviews={candidates}
                  busy={busy || generating}
                />
              </fieldset>
              {stage === 'design' &&
                draft.design &&
                !draft.design.options.some((o) => o.sections) && (
                  <div className="lpw-preview-actions">
                    <button
                      className="lpw-button secondary"
                      onClick={() => void showPreview()}
                      disabled={busy || generating}
                    >
                      ページ全体をプレビュー
                    </button>
                    <button
                      className="lpw-button secondary"
                      disabled={
                        busy ||
                        generating ||
                        batch.length > 0 ||
                        dirty ||
                        !draft.outline?.sections.some(
                          (s) =>
                            !project.workflow.assets[s.id] ||
                            project.workflow.assets[s.id].stale,
                        )
                      }
                      onClick={() =>
                        setBatch(
                          (draft.outline?.sections ?? [])
                            .filter(
                              (s) =>
                                !project.workflow.assets[s.id] ||
                                project.workflow.assets[s.id].stale,
                            )
                            .map((s) => s.id),
                        )
                      }
                    >
                      未生成・変更後の画像を順に生成
                    </button>
                  </div>
                )}
              {stage === 'review' && (
                <div className="lpw-preview-actions">
                  <button
                    className="lpw-button secondary"
                    onClick={() => void showPreview()}
                    disabled={busy || generating}
                  >
                    ページをプレビュー
                  </button>
                  <button
                    className="lpw-button primary"
                    onClick={() => void download()}
                    disabled={
                      busy ||
                      generating ||
                      dirty ||
                      !project.workflow.confirmed.review
                    }
                  >
                    <Download size={16} />
                    画像を含むHTMLを書き出す
                  </button>
                </div>
              )}
              {preview && (
                <div className="lpw-preview" ref={previewRef}>
                  {previewName && <h3>{previewName}のLPプレビュー</h3>}
                  <div className="lpw-preview-toolbar">
                    <button
                      className={!mobile ? 'selected' : ''}
                      onClick={() => setMobile(false)}
                    >
                      <Monitor size={16} />
                      PC
                    </button>
                    <button
                      className={mobile ? 'selected' : ''}
                      onClick={() => setMobile(true)}
                    >
                      <Smartphone size={16} />
                      スマホ
                    </button>
                    <span>
                      リンク先は「実際のCTA遷移先を開く」から確認できます。
                    </span>
                  </div>
                  <div className="lpw-preview-canvas">
                    <iframe
                      sandbox=""
                      className={mobile ? 'mobile' : ''}
                      title={
                        mobile ? 'LPのスマホプレビュー' : 'LPのPCプレビュー'
                      }
                      srcDoc={preview}
                    />
                  </div>
                </div>
              )}
              <div className="lpw-save-bar">
                <p>
                  {dirty && LP_STAGES.indexOf(stage) < 4
                    ? 'この内容を変更すると、後の工程は再確認が必要になります。'
                    : '確定した内容を、次の工程へ引き継ぎます。'}
                </p>
                <div>
                  <button
                    className="lpw-button secondary"
                    disabled={busy || generating || !draft[stage]}
                    onClick={() => void save(false)}
                  >
                    下書きを保存
                  </button>
                  <button
                    className="lpw-button primary"
                    disabled={
                      busy || generating || !draft[stage] || batch.length > 0
                    }
                    onClick={() => void save(true)}
                  >
                    {busy ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )}
                    {stage === 'review'
                      ? '最終確認を完了する'
                      : 'この内容で次へ'}
                    {stage !== 'review' && <ArrowRight size={16} />}
                  </button>
                </div>
              </div>
            </section>
          </>
        ) : !id ? (
          <div className="lpw-home">
            <LpWorkflowCreate onCreated={created} />
            <aside data-tutorial="ads-lp-results" className="lpw-card lpw-projects">
              <h2>制作中・保存済みのLP</h2>
              {projects.length === 0 ? (
                <p className="lpw-muted">まだLPがありません。</p>
              ) : (
                projects.map((item) => (
                  <button key={item.id} onClick={() => open(item.id)}>
                    <strong>
                      {item.title ||
                        item.product_info?.slice(0, 30) ||
                        '名称未設定のLP'}
                    </strong>
                    <span>
                      {item.workflow ? '6段階で制作' : '従来のLP'} ·{' '}
                      {new Date(item.updated_at).toLocaleDateString('ja-JP')}
                    </span>
                    <ArrowRight size={16} />
                  </button>
                ))
              )}
            </aside>
          </div>
        ) : null}
      </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
}
